import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Key } from "lucide-react";
import { z } from "zod";
import { authService } from "@/services/authService";
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import {
  AuthLayout,
  AuthCard,
  AuthCardHeader,
  AuthCardFooter,
  TextField,
  PasswordField,
  PrimaryButton,
  VisualPanel,
} from "@/components/auth";

type SupportLoginFormData = { email: string; password: string; supportToken: string };

const createSupportLoginSchema = () => z.object({
  email: z.string().email(translateInterfaceText("E-mail inválido")),
  password: z.string().min(1, translateInterfaceText("Campo obrigatório")),
  supportToken: z.string().min(1, translateInterfaceText("Código de acesso é obrigatório")),
});

const SupportLogin = () => {
  useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<SupportLoginFormData>({
    email: "",
    password: "",
    supportToken: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof SupportLoginFormData, string>>>({});
  const { login, refreshUser } = useCustomAuth();

  const handleChange = (field: keyof SupportLoginFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = createSupportLoginSchema().safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof SupportLoginFormData, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof SupportLoginFormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const res = await authService.supportLogin(
        { email: formData.email, senha: formData.password },
        formData.supportToken
      );

      if (res?.data?.success) {
        login(res.data.user, res.data.token!, res.data.plano, false);
        await refreshUser();
        toast.success(translateInterfaceText("Autenticado com sucesso!"));
        navigate("/dashboard", { replace: true });
      } else {
        toast.error(res?.data?.message || translateInterfaceText("Erro ao fazer login de suporte"));
      }
    } catch (error: any) {
      toast.error(error?.message || translateInterfaceText("Erro ao fazer login de suporte"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout visualPanel={<VisualPanel />}>
      <AuthCard>
        <img
          src="/assets/imgs/logo-azul-e-preta.png"
          alt={translateInterfaceText("HostVSL")}
          className="h-[34px] sm:h-[36px] lg:h-[42px] mb-[16px] lg:mx-auto"
        />

        <AuthCardHeader
          title={translateInterfaceText("Acesso de Suporte")}
          subtitle={translateInterfaceText("Entre com as credenciais do cliente e o código de acesso fornecido.")}
          className="mb-[20px] lg:text-center"
        />

        <form onSubmit={handleSubmit} className="w-full space-y-[12px] lg:max-w-[70%] lg:mx-auto">
          <TextField
            label={translateInterfaceText("E-mail do cliente")}
            type="email"
            placeholder="email@exemplo.com"
            value={formData.email}
            onChange={handleChange("email")}
            error={errors.email}
            icon={<Mail className="h-4 w-4" />}
            autoComplete="email"
          />

          <PasswordField
            label={translateInterfaceText("Senha do cliente")}
            placeholder="••••••••"
            value={formData.password}
            onChange={handleChange("password")}
            error={errors.password}
            autoComplete="current-password"
          />

          <TextField
            label={translateInterfaceText("Código de acesso")}
            type="text"
            placeholder={translateInterfaceText("Cole o código de acesso aqui")}
            value={formData.supportToken}
            onChange={handleChange("supportToken")}
            error={errors.supportToken}
            icon={<Key className="h-4 w-4" />}
            autoComplete="off"
          />

          <div className="pt-[14px]">
            <PrimaryButton type="submit" loading={isLoading} loadingText={translateInterfaceText("Entrando...")}>
              {translateInterfaceText("Entrar como suporte")}</PrimaryButton>
          </div>
        </form>

        <AuthCardFooter>
          <div className="flex items-center justify-center">
            <a
              href="/login"
              className="text-[13px] transition-colors hover:underline underline-offset-2"
              style={{ color: '#1F6BFF' }}
            >
              {translateInterfaceText("Voltar para o login")}</a>
          </div>
        </AuthCardFooter>
      </AuthCard>
    </AuthLayout>
  );
};

export default SupportLogin;
